<template>
  <div>
    <h1>My Students</h1>
    <p class="text-muted font-weight-bold">
      View and manage your student roster
    </p>
    <hr />

    <b-row>
      <b-col md="8">
        <b-form-group class="mb-2 w-100">
          <b-input-group class="input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input v-model="searchQuery" placeholder="Search students" />
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col md="4">
        <v-select
          class="bg-white mb-2"
          v-model="classID"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="classesOptions"
          :reduce="(opt) => opt.id"
          label="name"
          :clearable="false"
          :disabled="clsLoading"
          placeholder="Select class"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        md="6"
        lg="4"
        v-for="student in filteredStudents"
        :key="student.id"
      >
        <b-card no-body border-variant="light">
          <b-card-body>
            <!-- Header -->
            <div class="d-flex justify-content-between align-items-start mb-1">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="lg"
                  variant="light-secondary"
                  :text="student.initials"
                  class="mr-1 font-weight-bold"
                />
                <div>
                  <h5 class="mb-0 font-weight-bold text-dark">
                    {{ student.name }}
                  </h5>
                  <small>{{ student.grade }}</small>
                </div>
              </div>
              <!-- <feather-icon
                icon="MoreHorizontalIcon"
                class="text-muted cursor-pointer"
              /> -->
            </div>

            <!-- Badges -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <b-badge
                :variant="`light-${student.statusVariant}`"
                pill
                :class="`border-${student.statusVariant}`"
              >
                {{ student.status }}
              </b-badge>
              <b-badge v-if="student.atRisk" variant="danger" pill>
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="12"
                  class="mr-25"
                />
                At Risk
              </b-badge>
            </div>

            <!-- Stats -->
            <div class="p-1 mb-1">
              <b-row>
                <b-col
                  cols="6"
                  class="bg-light py-1 rounded border-right-white"
                  style="border-width: 12px"
                >
                  <small class="d-block mb-50">Overall Score</small>
                  <h4 class="mb-0 font-weight-bolder text-dark">
                    {{ student.score }}
                  </h4>
                </b-col>
                <b-col cols="6" class="bg-light py-1">
                  <small class="d-block mb-50">Growth</small>
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="TrendingUpIcon"
                      :class="`text-${student.statusVariant}`"
                      class="mr-50"
                    />
                    <h5
                      class="mb-0 font-weight-bolder"
                      :class="`text-${student.statusVariant}`"
                    >
                      {{ student.growth }}
                    </h5>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Actions -->
            <div class="d-flex align-items-center">
              <b-button
                :variant="`outline-${student.statusVariant}`"
                block
                class="mr-1"
                :class="`btn-hover-${student.statusVariant}`"
              >
                View Profile
              </b-button>
              <b-button
                variant="outline-secondary"
                class="btn-icon btn-hover-secondary"
              >
                <feather-icon icon="MailIcon" />
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BAvatar,
  BButton,
  BBadge,
} from "bootstrap-vue";

import vSelect from "vue-select";

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BAvatar,
    BButton,
    BBadge,
    vSelect,
  },
  data() {
    return {
      searchQuery: "",
      clsLoading: false,
      classID: 0,
      classesOptions: [
        { id: 0, name: "All Classes" },
        { id: 1, name: "Grade 1" },
        { id: 2, name: "Grade 2" },
        { id: 3, name: "Grade 3" },
      ],
      students: [
        {
          id: 1,
          name: "Marcus Johnson",
          initials: "MJ",
          grade: "Grade 5 • 5A",
          status: "Exceeds",
          statusVariant: "success",
          atRisk: false,
          score: "92%",
          growth: "85th",
        },
        {
          id: 2,
          name: "Aaliyah Williams",
          initials: "AW",
          grade: "Grade 5 • 5A",
          status: "Meets",
          statusVariant: "info",
          atRisk: false,
          score: "78%",
          growth: "72th",
        },
        {
          id: 3,
          name: "Diego Martinez",
          initials: "DM",
          grade: "Grade 5 • 5A",
          status: "Approaching",
          statusVariant: "warning",
          atRisk: true,
          score: "65%",
          growth: "45th",
        },
        {
          id: 4,
          name: "Emma Chen",
          initials: "EC",
          grade: "Grade 5 • 5A",
          status: "Exceeds",
          statusVariant: "success",
          atRisk: false,
          score: "95%",
          growth: "92th",
        },
        {
          id: 5,
          name: "Jaylen Thompson",
          initials: "JT",
          grade: "Grade 5 • 5A",
          status: "Below",
          statusVariant: "danger",
          atRisk: true,
          score: "52%",
          growth: "28th",
        },
        {
          id: 6,
          name: "Sofia Gonzalez",
          initials: "SG",
          grade: "Grade 5 • 5A",
          status: "Meets",
          statusVariant: "info",
          atRisk: false,
          score: "81%",
          growth: "68th",
        },
        {
          id: 7,
          name: "Lucas Brown",
          initials: "LB",
          grade: "Grade 5 • 5A",
          status: "Meets",
          statusVariant: "info",
          atRisk: false,
          score: "75%",
          growth: "60th",
        },
        {
          id: 8,
          name: "Isabella Rossi",
          initials: "IR",
          grade: "Grade 5 • 5A",
          status: "Exceeds",
          statusVariant: "success",
          atRisk: false,
          score: "88%",
          growth: "82th",
        },
        {
          id: 9,
          name: "Kevin Nguyen",
          initials: "KN",
          grade: "Grade 5 • 5A",
          status: "Approaching",
          statusVariant: "warning",
          atRisk: true,
          score: "62%",
          growth: "40th",
        },
        {
          id: 10,
          name: "Maya Patel",
          initials: "MP",
          grade: "Grade 5 • 5A",
          status: "Below",
          statusVariant: "danger",
          atRisk: true,
          score: "48%",
          growth: "22th",
        },
      ],
    };
  },
  computed: {
    filteredStudents() {
      return this.students.filter((student) => {
        return (
          student.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          student.grade.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      });
    },
  },
};
</script>

<style scoped>
.btn-hover-primary:hover {
  background-color: var(--primary) !important;
  color: #fff !important;
}
.btn-hover-secondary:hover {
  background-color: var(--secondary) !important;
  color: #fff !important;
}
.btn-hover-success:hover {
  background-color: var(--success) !important;
  color: #fff !important;
}
.btn-hover-info:hover {
  background-color: var(--info) !important;
  color: #fff !important;
}
.btn-hover-warning:hover {
  background-color: var(--warning) !important;
  color: #fff !important;
}
.btn-hover-danger:hover {
  background-color: var(--danger) !important;
  color: #fff !important;
}
</style>
